import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { delay, Waiter, withIndex } from '@gametailors/web-core/misc';
import { RidCompressableKlankCard } from '../../components/rid-compressable-klank-card/rid-compressable-klank-card';
import { RidCorrectAnswerModal } from '../../components/rid-correct-answer-modal/rid-correct-answer-modal';
import { RidFunctionalSyllabicKeyboard, TRule } from '../../components/rid-functional-syllabic-keyboard/rid-functional-syllabic-keyboard';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import { RidOption, TOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSyllabicCard } from '../../components/rid-syllabic-card/rid-syllabic-card';
import {
  RidSyllabicKeyboardUtils,
  TKey,
  TKeyboardLayout,
  TKeyValue,
} from '../../components/rid-syllabic-keyboard/rid-syllabic-keyboard.utils';
import { RidTextInput } from '../../components/rid-text-input/rid-text-input';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidVerticalSpacer } from '../../components/rid-vertical-spacer/rid-vertical-spacer';

@Component({
  selector: 'rid-syllabic-keyboard-assignments',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidOption,
    RidTextInput,
    RidVerticalSpacer,
    RidCompressableKlankCard,
    RidSyllabicCard,
    RidOverlayFeedback,
    RidModalBackground,
    RidCorrectAnswerModal,
    RidFunctionalSyllabicKeyboard,
  ],
  templateUrl: './rid-syllabic-keyboard-assignments.html',
  host: {
    class: 'block',
  },
})
export class RidSyllabicKeyboardAssignments {
  name = input.required<string>();
  prompts = input.required<{ prompt: TOption; answer: TKeyValue[] }[]>();
  layout = input<TKeyboardLayout | undefined>();
  mask = input<TKeyValue[] | undefined>();
  mode = input<'GROUP' | 'KLANK' | 'BOTH'>('BOTH');

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  keyboardComp = viewChild.required<RidFunctionalSyllabicKeyboard>('keyboardComp');

  promptIndex = signal(0);
  promptKlankIndex = signal(0);
  progress = signal(0);
  rawPrompt = computed(() => this.prompts()[this.promptIndex()]);
  prompt = computed(() => {
    const prompt: { prompt: TOption; answer: TKeyValue[] } = {
      prompt: this.rawPrompt().prompt,
      answer: [],
    };

    for (const [key, i] of withIndex(this.rawPrompt().answer)) {
      prompt.answer.push(key);

      if (key === '+') {
        prompt.answer.push(this.rawPrompt().answer[i + 1]);
      }
    }

    return prompt;
  });
  appearance = signal<'DEFAULT' | 'CORRECT' | 'INCORRECT'>('DEFAULT');
  revelation = signal<TKey[] | undefined>(undefined);
  attempts = signal<Map<number, number>>(new Map());

  appliedRule = output<TRule>();
  finished = output<void>();

  convertValueToKey = RidSyllabicKeyboardUtils.convertValueToKey.bind(RidSyllabicKeyboardUtils);
  readonly revealWaiter = new Waiter();

  validate(key: TKey) {
    const value = this.prompt().answer[this.promptKlankIndex()];
    return key.value === value;
  }

  async onIncorrect() {
    this.overlayFeedback().showIncorrect();
    this.attempts.update((a) => new Map(a).set(this.promptKlankIndex(), (a.get(this.promptKlankIndex()) ?? 0) + 1));

    if (Array.from(this.attempts().values()).reduce((acc, cur) => acc + cur, 0) >= 5) {
      await this.reveal();
      this.goToNextPrompt();
    }
  }

  async onInsert(key: TKey) {
    this.promptKlankIndex.update((j) => j + 1);

    const next = this.prompt().answer.at(this.promptKlankIndex());
    const nextKey = next ? RidSyllabicKeyboardUtils.convertValueToKey(next) : undefined;

    if (!nextKey) {
      await delay(500);
      this.goToNextPrompt();
      return;
    }

    if (nextKey.type === 'RULE') {
      this.keyboardComp().press(nextKey);
    }
  }

  onApplyRule(rule: TRule) {
    if (rule.name !== 'MEDEKLINKER') {
      this.promptKlankIndex.update((j) => j + 1);
    }

    this.appliedRule.emit(rule);
  }

  goToNextPrompt() {
    const isLast = this.promptIndex() + 1 >= this.prompts().length;
    if (isLast) {
      this.finish();
      return;
    }

    this.promptIndex.update((i) => i + 1);
    this.promptKlankIndex.set(0);
    this.progress.update((p) => p + 1);
    this.keyboardComp().clear();
    this.attempts.set(new Map());
  }

  async reveal() {
    this.revelation.set(
      this.prompt()
        .answer.map((v) => RidSyllabicKeyboardUtils.convertValueToKey(v))
        .filter((k) => k.type !== 'RULE'),
    );

    await this.revealWaiter.wait();

    this.revelation.set(undefined);
  }

  finish() {
    this.finished.emit();
  }
}
